import { For, type JSX } from 'solid-js';
import { createStore } from 'solid-js/store';
import { t } from '../i18n';
import { errorReason } from '../lib/api';
import { linkRequestRefusal, sendLinkRequest, useLinkCandidates } from '../lib/link-candidates';
import { LinkCandidateRow, type LinkRowState } from './LinkCandidateRow';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';

// "Other sign-ins with your email" on Connected logins (#7426 decision 2).
// CONTAINER: reads `GET /v1/me/link-candidates`, asks
// `POST /v1/me/link-requests` per row, and keeps each row's send state in a
// store keyed by the candidate's user id — one row's refusal never paints
// another. The bind itself is the mailed link opened in this browser; nothing
// here changes the account, so a 202 re-reads nothing. A `not_a_candidate`
// refusal DOES re-read: the list the person acted on is stale.
// `<LinkCandidateRow/>` renders.

export function LinkCandidatesPanel(props: { readonly token: string | null }): JSX.Element {
  const query = useLinkCandidates(props.token);
  const [rows, setRows] = createStore<Record<string, LinkRowState>>({});

  const send = async (userId: string): Promise<void> => {
    if (rows[userId]?.kind === 'sending') return;
    setRows(userId, { kind: 'sending' });
    try {
      const sent = await sendLinkRequest(userId, props.token);
      setRows(userId, { kind: 'sent', sent });
    } catch (err) {
      const refusal = linkRequestRefusal(err);
      setRows(userId, {
        kind: 'refused',
        refusal,
        message: errorReason(err, t('logins.candidate_send_error')),
      });
      if (refusal.kind === 'not_a_candidate') await query.refetch();
    }
  };

  return (
    <Panel title={t('logins.candidates_title')} class="link-candidates">
      <p class="identity-links__lead">{t('logins.candidates_desc')}</p>
      <QueryBoundary
        query={query}
        loading={<Skeleton height="4rem" />}
        isEmpty={(data) => data.length === 0}
        empty={<p class="list-empty">{t('logins.candidates_empty')}</p>}
      >
        {(candidates) => (
          <ul class="link-candidates__list">
            <For each={candidates}>
              {(c) => (
                <LinkCandidateRow
                  candidate={c}
                  state={rows[c.userId] ?? { kind: 'idle' }}
                  onSend={() => void send(c.userId)}
                />
              )}
            </For>
          </ul>
        )}
      </QueryBoundary>
    </Panel>
  );
}
